"use client";

import React, { ComponentType, ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { AlertTriangle, Inbox } from "lucide-react";

/**
 * Props for data fetching wrapper components
 */
interface DataFetchingProps {
  isLoading: boolean;
  error: Error | null;
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  emptyComponent?: ReactNode;
  onRetry?: () => void | Promise<any>;
}

/**
 * Reusable data fetching wrapper with loading, error, and empty states
 */
export function DataFetchingWrapper({
  isLoading,
  error,
  isEmpty = false,
  emptyMessage = "No data found",
  children,
  loadingComponent,
  errorComponent,
  emptyComponent,
  onRetry,
}: DataFetchingProps) {
  if (isLoading) {
    return (
      loadingComponent || (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )
    );
  }

  if (error) {
    return (
      errorComponent || (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40">
          <CardContent className="p-6">
            <div className="text-center text-red-600 space-y-3">
              <AlertTriangle className="h-8 w-8 mx-auto" />
              <div>
                <p className="text-sm">Failed to load data</p>
                <p className="text-xs text-red-500 mt-1">{error.message}</p>
              </div>
              {onRetry && (
                <button
                  type="button"
                  onClick={() => void onRetry()}
                  className="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  Retry
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )
    );
  }

  if (isEmpty) {
    return (
      emptyComponent || <EmptyStateCard message={emptyMessage} />
    );
  }

  return <>{children}</>;
}

/**
 * Standard loading skeleton for cards
 */
export function CardSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(count)].map((_, i) => (
        <Card key={i} className="animate-pulse">
          <CardContent className="p-6">
            <div className="h-4 bg-card-200 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-card-200 rounded w-1/2"></div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Standard error card
 */
export function ErrorCard({
  message = "Failed to load data",
  details,
}: {
  message?: string;
  details?: string;
}) {
  return (
    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40">
      <CardContent className="p-6">
        <div className="text-center text-red-600">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">{message}</p>
          {details && <p className="text-xs text-red-500 mt-1">{details}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Standard empty state card
 */
export function EmptyStateCard({
  message = "No data found",
  title,
  description,
  icon: Icon = Inbox,
  action,
  className,
}: {
  message?: string;
  title?: string;
  description?: string;
  /** Any lucide-style icon component; rendered with sizing/colour classes. */
  icon?: ComponentType<{ className?: string }>;
  action?: ReactNode;
  className?: string;
}) {
  const displayTitle = title || message;

  return (
    <Card className={className}>
      <CardContent className="flex flex-col items-center justify-center p-6 text-center">
        <div className="rounded-full bg-muted/50 p-4 mb-4">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">{displayTitle}</h3>
        {description && (
          <p className="text-sm text-muted-foreground max-w-sm mb-4">{description}</p>
        )}
        {action && <div className="mt-2">{action}</div>}
      </CardContent>
    </Card>
  );
}

/**
 * Error boundary class component for SafeComponent
 */
class SafeComponentErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback: React.ReactNode;
    onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    children: React.ReactNode;
    fallback: React.ReactNode;
    onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <>{this.props.fallback}</>;
    }
    return <>{this.props.children}</>;
  }
}

/**
 * Safe component wrapper that catches errors and displays a fallback
 */
export function SafeComponent({
  children,
  fallback = <div className="p-4 text-center text-gray-500">Something went wrong</div>,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const [hasError, setHasError] = React.useState(false);

  const handleError = React.useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    console.error("SafeComponent caught React error:", error, errorInfo);
    setHasError(true);
  }, []);

  React.useEffect(() => {
    const handleWindowError = (error: ErrorEvent) => {
      console.error("SafeComponent caught window error:", error);
      setHasError(true);
    };

    window.addEventListener("error", handleWindowError);
    return () => window.removeEventListener("error", handleWindowError);
  }, []);

  if (hasError) {
    return <>{fallback}</>;
  }

  return (
    <SafeComponentErrorBoundary fallback={fallback} onError={handleError}>
      {children}
    </SafeComponentErrorBoundary>
  );
}

/**
 * Safe component wrapper with automatic retry
 */
export function SafeComponentWithRetry({
  children,
  maxRetries = 3,
  fallback = <div className="p-4 text-center text-gray-500">Something went wrong</div>,
}: {
  children: React.ReactNode;
  maxRetries?: number;
  fallback?: React.ReactNode;
}) {
  const [retryCount, setRetryCount] = React.useState(0);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error("SafeComponentWithRetry caught error:", error);
      if (retryCount < maxRetries) {
        setRetryCount((prev) => prev + 1);
        // Reset error state to allow retry
        setHasError(false);
      } else {
        setHasError(true);
      }
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, [retryCount, maxRetries]);

  if (hasError) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
